import React from 'react';

/* ──────────────────────────────────────────────────────────────────
 * BillingPaySidebar — 320px left panel
 *
 * From the Claude Design billing handoff. Card-wrapped column with a
 * header row (title + `+ New` sage button) and a reverse-chronological
 * list of pay apps below. Each card shows AFP # · month, period date
 * range, status pill, and (when finalized) a CE-sync chip with an
 * inline Retry button if the sync failed.
 *
 * Selection: 2px sage left border + accent-soft fill. Unselected rows
 * carry a 2px transparent border so the selected/unselected rows don't
 * cause text-shift.
 * ────────────────────────────────────────────────────────────────── */

const fmtMonthYear = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};
const fmtDateMD = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DollarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const PlusIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const Spinner = ({ size = 11, color = 'currentColor' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const StatusPill = ({ status, superseded }) => {
  // A superseded (revised) AFP shows a "Void" pill regardless of its
  // underlying Finalized status — the revised copy is the live one.
  if (superseded) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 9px', borderRadius: 999,
        fontSize: 12.5, fontWeight: 600, color: 'var(--danger)',
        background: 'var(--danger-soft)',
        border: '1px solid color-mix(in oklch, var(--danger) 25%, transparent)',
        letterSpacing: '0.03em', whiteSpace: 'nowrap', textTransform: 'uppercase',
      }}>Void</span>
    );
  }
  const isDraft = status === 'Draft';
  const fg = isDraft ? 'var(--text-3)' : 'var(--success)';
  const bg = isDraft ? 'var(--bg-surface-2)' : 'var(--success-soft)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px 2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500, color: fg, background: bg,
      border: `1px solid color-mix(in oklch, ${fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: fg }} />
      {status}
    </span>
  );
};

// CESyncChip + Retry button removed alongside the BillingG702 chip
// + BillingFooterActions Retry button. A dedicated CE sync admin
// tab will surface these — keep the API helper (retryBillingCESync)
// available in api/index.js for that tab.

const BillingPaySidebar = ({
  periods,
  selectedId,
  onSelect,
  onCreate,
  loading,
}) => {
  const sorted = [...(periods || [])].sort((a, b) => {
    const aEnd = a.period_end_date || a.period_start_date || '';
    const bEnd = b.period_end_date || b.period_start_date || '';
    return bEnd.localeCompare(aEnd);
  });

  return (
    <div style={{
      width: 320, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-surface)',
      borderRadius: 12,
      border: '1px solid var(--border-subtle)',
      boxShadow: 'var(--shadow-sm)',
      overflow: 'hidden',
      alignSelf: 'flex-start',
      maxHeight: 'calc(100vh - 220px)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 14px',
        background: 'var(--bg-surface-2)',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}>
        <h3 style={{
          margin: 0, fontSize: 12, fontWeight: 600,
          color: 'var(--text-1)', textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>Pay Applications</h3>
        <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)' }}>
          {sorted.length}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onCreate}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            height: 26, padding: '0 10px', borderRadius: 6,
            fontSize: 13, fontWeight: 500, color: '#fff',
            background: 'var(--accent)',
            border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
            boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent)'}
        >
          <PlusIcon /> New
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 80 }}>
        {loading ? (
          <div style={{ padding: '24px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--text-3)', fontSize: 14 }}>
            <Spinner size={14} color="var(--accent)" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div style={{
            padding: '32px 20px', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'var(--accent-soft)', color: 'var(--accent-text)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <DollarIcon />
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)' }}>No pay applications yet</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.4 }}>
              Create one to start billing this project.
            </div>
            <button
              type="button"
              onClick={onCreate}
              style={{
                marginTop: 4,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                height: 30, padding: '0 12px', borderRadius: 7,
                fontSize: 13.5, fontWeight: 500, color: '#fff',
                background: 'var(--accent)',
                border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                cursor: 'pointer',
              }}
            >
              <PlusIcon /> New pay application
            </button>
          </div>
        ) : (
          sorted.map((p, i) => {
            const isSel = String(p.id) === String(selectedId);
            const isLast = i === sorted.length - 1;
            const isVoid = !!p.superseded;
            return (
              <div
                key={p.id}
                onClick={() => onSelect(p)}
                style={{
                  padding: '10px 14px 12px',
                  borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                  borderLeft: isSel ? '2px solid var(--accent)' : '2px solid transparent',
                  background: isSel ? 'var(--accent-soft)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background .12s',
                  // Superseded (revised → "Void") rows dim so the live
                  // revised copy reads as the active AFP. Still clickable.
                  opacity: isVoid ? 0.55 : 1,
                }}
                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span className="mono" style={{
                    fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)',
                    textTransform: 'uppercase', letterSpacing: '0.02em',
                    textDecoration: isVoid ? 'line-through' : 'none',
                  }}>
                    {p.period_name || `AFP #${p.id}`}{p.period_end_date ? ` · ${fmtMonthYear(p.period_end_date)}` : ''}
                  </span>
                  <div style={{ flex: 1 }} />
                  <StatusPill status={p.status} superseded={isVoid} />
                </div>
                <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 4 }}>
                  {p.period_start_date ? fmtDateMD(p.period_start_date) : '–'}
                  <span style={{ margin: '0 6px', color: 'var(--text-4)' }}>→</span>
                  {p.period_end_date ? fmtDateMD(p.period_end_date) : '–'}
                </div>
                {/* CE-sync chip + Retry button removed for now — the
                    ce_synced field was firing false-positive "Needs CE
                    Sync" alerts when Azure cut the connection mid-
                    request, even though the agent had processed the
                    job. Bring back once the status plumbing is solid. */}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BillingPaySidebar;
